import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Calendar, DollarSign, Shield, TrendingUp, Wrench, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OngoingSupportForm from '../components/OngoingSupportForm';
import BookingModal from '../components/BookingModal';
import usePageMeta from '../hooks/usePageMeta';

const OngoingSupport = () => {
  usePageMeta({
    title: 'Pricing | AI Office Manager Monthly Plans for HVAC, Plumbing & Electrical',
    description:
      'Starter, Growth, and Scale plans with unlimited calls, weekly AI tuning, and ServiceTitan/HCP/Jobber monitoring. Transparent monthly pricing for field service contractors.',
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // holds tier id while redirecting
  const [checkoutError, setCheckoutError] = useState('');

  const startCheckout = async (packageId) => {
    setCheckoutError('');
    setCheckoutLoading(packageId);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(`${apiUrl}/api/payments/checkout/session`, {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Missing checkout URL');
      }
    } catch (err) {
      setCheckoutError(err?.response?.data?.detail || err.message || 'Checkout failed');
      setCheckoutLoading(null);
    }
  };

  const whySupport = [
    { icon: Wrench, title: 'Script Drift', desc: 'Your prices, service menu, and techs change. Your AI has to change with them — or it starts booking wrong' },
    { icon: Shield, title: 'Integration Breaks', desc: 'CRMs push updates that break integrations. We monitor 24/7 and fix before you notice' },
    { icon: TrendingUp, title: 'Call-Quality Tuning', desc: 'Every week we listen to recordings, flag missed wins, and tune the AI to close more jobs' }
  ];

  const included = [
    'Unlimited call minutes — no overage bills',
    '24/7 system monitoring with real-time alerts',
    'Weekly call-recording review & AI tuning',
    'Monthly performance report (missed vs. booked jobs)',
    'Script updates when your pricing or services change',
    'CRM integration maintenance (ServiceTitan, HCP, Jobber)',
    'Priority support (4-hour response or faster)',
    'Monthly strategy call with your account manager'
  ];

  const supportTiers = [
    {
      name: 'Starter',
      packageId: 'starter',
      price: '$897',
      original: '$997',
      features: [
        'Up to 500 calls/month',
        'Monthly performance report',
        'Standard monitoring',
        'Email support',
        'Best for: Shops doing up to $1M/yr'
      ]
    },
    {
      name: 'Growth',
      packageId: 'growth',
      price: '$1,497',
      original: '$1,697',
      popular: true,
      features: [
        'Up to 2,000 calls/month',
        'Weekly call-recording review',
        '24/7 monitoring & alerts',
        'Priority support (4hr response)',
        'Monthly strategy call',
        'Best for: Shops doing $1M-$5M/yr'
      ]
    },
    {
      name: 'Scale',
      packageId: 'scale',
      price: '$2,997',
      original: '$3,497',
      features: [
        'Unlimited calls',
        'Daily call-quality review',
        'Real-time integration monitoring',
        'Dedicated account manager (1hr response)',
        'Weekly strategy calls',
        'Custom SLA',
        'Best for: Multi-location shops $5M+/yr'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header onOpenQuiz={() => {}} />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#00E599] hover:text-[#00C985] font-body text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00E599]/10 border border-[#00E599]/20 rounded-full text-[#00E599] text-sm font-accent mb-6">
              24/7 AI Office Manager Monitoring & Tuning
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold text-[#F4F4F5] leading-tight tracking-tighter mb-6">
              We Keep Your AI Office Manager
              <span className="block mt-2 gradient-text">Booking Jobs While You Sleep</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#A1A1AA] font-body leading-relaxed mb-8">
              Set-and-forget doesn't exist in voice AI. Prices change, techs quit, CRMs update. We listen to every call, tune the script weekly, and fix integration breaks before you ever know they happened.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                Get a Plan Quote
              </Button>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setIsBookingOpen(true);
                }}
                data-testid="ongoing-schedule-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 glass rounded-lg text-sm sm:text-base text-[#F4F4F5] font-body font-semibold hover:border-[#00E599]/30 transition-all cursor-pointer"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                Schedule Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Support Matters */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              Why Voice AI Needs Weekly Attention
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-3xl mx-auto">
              The shops with the best AI performance aren't the ones with the fanciest tech — they're the ones who tune it every single week.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {whySupport.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass p-6 rounded-xl text-center"
              >
                <item.icon className="w-12 h-12 text-[#00E599] mx-auto mb-4" />
                <h3 className="text-xl font-headline font-bold text-[#F4F4F5] mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] font-body">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              What's Included Every Month
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-2xl mx-auto">
              Every plan includes the tuning, monitoring, and support that keeps your AI earning its keep.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {included.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex items-start gap-3 glass p-4 rounded-lg"
              >
                <Check className="w-6 h-6 text-[#00E599] flex-shrink-0 mt-0.5" />
                <span className="text-[#F4F4F5] font-body">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Tiers */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              Monthly Plans
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-2xl mx-auto">
              Pick the plan that fits your call volume. Every plan includes the $4,500 one-time setup.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6" data-testid="pricing-tiers">
            {checkoutError && (
              <div
                className="col-span-full text-center text-red-400 font-body text-sm"
                data-testid="checkout-error"
              >
                {checkoutError}
              </div>
            )}
            {supportTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`glass p-8 rounded-2xl ${tier.popular ? 'border-2 border-[#00E599] relative' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E599] text-[#050505] text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-headline font-bold text-[#F4F4F5] mb-4">{tier.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-headline font-bold gradient-text">{tier.price}</span>
                  <span className="text-sm text-[#A1A1AA]">/month</span>
                </div>
                <p className="text-sm text-[#A1A1AA] line-through mb-6">{tier.original}/month</p>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#00E599] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#F4F4F5] font-body">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => startCheckout(tier.packageId)}
                  disabled={checkoutLoading === tier.packageId}
                  data-testid={`tier-checkout-${tier.packageId}`}
                  className={`w-full whitespace-nowrap ${tier.popular ? 'bg-[#00E599] hover:bg-[#00C985] text-[#050505]' : 'glass text-[#F4F4F5]'} px-4 sm:px-6 py-3 text-sm sm:text-base font-body font-semibold rounded-lg transition-all disabled:opacity-60`}
                >
                  {checkoutLoading === tier.packageId ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting…
                    </span>
                  ) : (
                    `Get ${tier.name} — Pay Now`
                  )}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-6">
              Ready to Never Miss Another Job?
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body mb-8">
              Book a consultation. We'll review your call volume and match you to the right plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                Request Monthly Plan
              </Button>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setIsBookingOpen(true);
                }}
                data-testid="ongoing-schedule-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 glass rounded-lg text-sm sm:text-base text-[#F4F4F5] font-body font-semibold hover:border-[#00E599]/30 transition-all cursor-pointer"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                Schedule Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <OngoingSupportForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </div>
  );
};

export default OngoingSupport;
